// apps/amsuite/src/app/models/workspace-dashboard.interface.ts

// ── Contratti REST del servizio Workspace (/v1/dashboards) ──
// .NET serializza in camelCase (System.Text.Json Web defaults).

export interface WorkspaceDashboardWidgetDto {
  id: number;                 // Id della riga di associazione (int, legacy awi)
  widgetId: string;           // GUID del widget nel catalogo
  position: string | null;    // JSON layout: {x,y,cols,rows,selector,idx,version}
  paramValues: string | null; // JSON parametri widget
  widgetSelector: string | null;
}

export interface WorkspaceDashboardDto {
  id: string;                 // GUID della dashboard
  appId: string;              // GUID app/modulo
  level: number;              // 0=System, 1=Tenant, 2=User
  ownerPartyId: string | null;
  userName: string | null;
  name: string;
  isDefault: boolean;
  obsolete: boolean;
  createDate: string;
  createUser: string | null;
  modifyDate: string | null;
  modifyUser: string | null;
  widgets: WorkspaceDashboardWidgetDto[];
}

export interface WorkspaceDashboardWidgetInput {
  widgetId: string;
  position: string | null;
  paramValues: string | null;
}

export interface CreateWorkspaceDashboardRequest {
  appId: string;
  name: string;
  widgets: WorkspaceDashboardWidgetInput[];
}

export interface UpdateWorkspaceDashboardRequest {
  name: string;
  widgets: WorkspaceDashboardWidgetInput[];
}

// ── Shape "legacy" attesa dai componenti dashboard (retro-compat dell'adapter) ──
export interface LegacyDashboardWidget {
  id: string;                              // widget id (era Widget.id)
  ownerAppId: string;
  selector: string;
  dashboardPosition: string;               // JSON
  dashboardAssociationParamValue: string;  // JSON/string
  dashboardAssociationId?: number;
}

export interface LegacyDashboard {
  id: string;
  appId: string;
  name: string;
  default: boolean;                        // NB: 'default', non 'isDefault'
  widgets: LegacyDashboardWidget[];
}

// ── Livello dashboard/template (allineato a DashboardDto.level del backend) ──
export const DashboardLevel = {
  System: 0,
  Tenant: 1,
  User: 2
} as const;

export type DashboardLevel = (typeof DashboardLevel)[keyof typeof DashboardLevel];

// ── GET /v1/me (WorkspaceMeService) ──
export interface WorkspaceCapabilitiesDto {
  canCreatePersonalDashboard: boolean;
  canManageTenantTemplates: boolean;
  canManageSystemTemplates: boolean;
}

export interface WorkspaceAppRolesDto {
  partyOwnerId: string;
  partyOwnerName: string | null;
  roles: string[];
}

export interface WorkspaceMeDto {
  currentUser: string | null;
  impersonation: boolean;
  partyId: string | null;
  fullName: string | null;
  legacyUsername: string | null;
  roles: string[];
  allAppRoles: WorkspaceAppRolesDto[];
  capabilities: WorkspaceCapabilitiesDto;
}

// ── GET /v1/dashboards/templates e GET /v1/admin/templates ──
// level: 0=System, 1=Tenant (i template utilizzabili/gestibili non sono mai a livello User).
export interface AvailableTemplateDto {
  id: string;          // GUID del template
  appId: string;       // GUID app/modulo
  level: number;       // 0=System, 1=Tenant
  name: string;
  isDefault: boolean;
  widgetCount: number;
}

// ── Request bodies per /v1/admin/templates/{tenant|system} ──
// Il backend accetta lo stesso shape di CreateWorkspaceDashboardRequest + isDefault.
export interface CreateTemplateRequest {
  appId: string;
  name: string;
  widgets: WorkspaceDashboardWidgetInput[];
  isDefault: boolean;
}

export interface UpdateTemplateRequest {
  name: string;
  widgets: WorkspaceDashboardWidgetInput[];
  isDefault?: boolean;
}

// ── Request body per POST /v1/dashboards/from-template ──
export interface CreateFromTemplateRequest {
  templateId: string;
  name?: string;
}

// ── Feature "Salva come" (UI): destinazione del salvataggio ──
export type SaveAsTarget = 'personal' | 'tenant' | 'system';

export interface SaveAsResult {
  target: SaveAsTarget;
  name: string;
  isDefault: boolean;
}
